'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { getLatestMessagesAction } from '../shared/notificationActions';
import { useTranslations } from 'next-intl';

interface Message {
  msg_id: string;
  user_id: string;
  msg_content: string;
  create_date: string;
  update_date: string;
  is_read: boolean;
}

interface NotificationPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function NotificationPopup({ isOpen, onClose }: NotificationPopupProps) {
  const t = useTranslations('recentChat');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Load messages when popup opens
  useEffect(() => {
    if (isOpen) {
      loadMessages();
    }
  }, [isOpen]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close popup on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const loadMessages = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await getLatestMessagesAction();
      
      if (result.success && result.messages) {
        setMessages(result.messages);
      } else {
        setError(result.message || t('failedToLoadMessages'));
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      setError(t('errorLoadingMessages'));
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      return t('minuteAgo', { minutes: diffInMinutes });
    } else if (diffInHours < 24) {
      return diffInHours === 1 
        ? t('hourAgo', { hours: diffInHours })
        : t('hoursAgoPlural', { hours: diffInHours });
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return diffInDays === 1
        ? t('dayAgo', { days: diffInDays })
        : t('daysAgoPlural', { days: diffInDays });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />
      
      {/* Popup */}
      <div
        ref={popupRef}
        className="relative w-96 max-h-[80vh] bg-[#1a1a1e] rounded-xl border border-[#ffffff1a] shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#ffffff1a]">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-white" />
            <h3 className="text-white font-semibold text-lg">{t('notifications')}</h3>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="w-8 h-8 text-white hover:bg-[#ffffff1a] rounded-lg"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto max-h-[calc(80vh-80px)]">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-white" />
                <span className="text-white">{t('loadingNotifications')}</span>
              </div>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-400 mb-4">{error}</p>
              <Button
                onClick={loadMessages}
                className="bg-[#ffffff1a] text-white hover:bg-[#ffffff2a]"
              >
                {t('tryAgain')}
              </Button>
            </div>
          ) : messages.length === 0 ? (
            <div className="p-6 text-center">
              <Bell className="w-12 h-12 text-gray-500 mx-auto mb-3" />
              <p className="text-gray-400">{t('noNotificationsYet')}</p>
            </div>
          ) : (
            <div className="p-2">
              {messages.map((message) => (
                <Card
                  key={message.msg_id}
                  className="mb-2 p-4 bg-[#222327] border-[#ffffff0a] hover:bg-[#2a2a2e] transition-colors cursor-pointer"
                >
                  <div className="flex flex-col gap-2">
                    <p className="text-white text-sm leading-relaxed">
                      {message.msg_content}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs">
                        {formatDate(message.create_date)}
                      </span>
                      {!message.is_read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {messages.length > 0 && (
          <div className="p-4 border-t border-[#ffffff1a]">
            <Button
              onClick={loadMessages}
              variant="ghost"
              className="w-full text-white hover:bg-[#ffffff1a]"
            >
              {t('refresh')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
} 